import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import React from 'react';

import { CarouselEventNavigation } from 'src/domains/layouts/webview/components/eventNavigation/CarouselEventNavigation';
import { observer } from 'src/utils/mobx-react';
import { LayoutSkeleton } from 'src/domains/layouts/webview/modules/LayoutSkeleton';
import {
    BackButton,
    EventNavigation,
} from 'src/domains/layouts/webview/components/eventNavigation/CarouselSelection.style';

import { SportCompetitionEventsList } from 'src/domains/layouts/webview/components/sportEventsList/SportEventsList';
import {
    getHeaderOptions,
    getDefaultHeaderOptions,
} from 'src/domains/layouts/webview/components/sports/general/config';
import { SportHeaderStar } from 'src/domains/layouts/webview/components/sportHeader/SportHeader';
import { EventsTableMobX } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX';
import { EventsTableUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableUnified';
import { Wrapper, SectionWrapper } from 'src/domains/sportsbook/webview/layouts/competitionPage/CompetitionPage.style';
import { CompetitionRouteType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SportsNavigation } from 'src/domains/layouts/webview/components/eventNavigation/SportsNavigation';

interface CompetitionPageGeneralPropsType {
    sportId: string;
    competitionId: CompetitionId;
    isCompetitionPage: boolean;
}

const CompetitionPageGeneral = observer(
    'CompetitionPageGeneral',
    (props: CompetitionPageGeneralPropsType): JSX.Element | null => {
        const { sportId, competitionId, isCompetitionPage } = props;

        const optionItem = getHeaderOptions();
        const options = optionItem[sportId] ?? getDefaultHeaderOptions();

        const common = useCommon();
        const { unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;

        if (unifiedSportEventsTable) {
            return (
                <section className='competition-general'>
                    <SportHeaderStar
                        options={options}
                        sportId={sportId}
                        isCompetitionPage={isCompetitionPage}
                        competitionId={competitionId}
                    />
                    <EventsTableUnified
                        competitionId={competitionId}
                        sportId={sportId}
                    />
                </section>
            );
        }

        if (sportId === 'football') {
            return (
                <section className='competition-general'>
                    <SportHeaderStar
                        options={options}
                        sportId={sportId}
                        isCompetitionPage={isCompetitionPage}
                        competitionId={competitionId}
                    />
                    <EventsTableMobX
                        competitionId={competitionId}
                        sportId={sportId}
                    />
                </section>
            );
        }

        return (
            <section className='competition-general'>
                <SportHeaderStar
                    options={options}
                    sportId={sportId}
                    isCompetitionPage={isCompetitionPage}
                    competitionId={competitionId}
                />
                <SportCompetitionEventsList
                    sport={sportId}
                    competition={competitionId}
                    isCompetitionPage={isCompetitionPage}
                />
            </section>
        );
    }
);

interface CompetitionPagePropsType {
    currentView: CompetitionRouteType;
    accountView: RightHandSideViewType | null;
    isSearchActive: boolean;
}

export const CompetitionPage = observer('CompetitionPage', (props: CompetitionPagePropsType) => {
    const { currentView, accountView, isSearchActive } = props;
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const { componentVariants, unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;

    const renderCompetitionPageNavigation = (currentView: CompetitionRouteType): React.ReactNode => {
        const common = useCommon();
        const starRouter = StarRouter.get(common);
        const sportId = currentView.slug;

        if (unifiedSportEventsTable) {
            return <SportsNavigation currentView={currentView} />;
        }

        const handleRedirectToSportPage = (): void => {
            starRouter.redirectToSportPage(sportId);
        };

        return (
            <EventNavigation>
                <BackButton
                    onClick={handleRedirectToSportPage}
                    position='left'
                />

                <CarouselEventNavigation currentView={currentView} />
            </EventNavigation>
        );
    };

    return (
        <SectionWrapper>
            <LayoutSkeleton
                accountView={accountView}
                currentView={currentView}
                isSearchActive={isSearchActive}
                navigationType='global'
            >
                <Wrapper variant={componentVariants.sportsbookVariant}>
                    {renderCompetitionPageNavigation(currentView)}

                    <CompetitionPageGeneral
                        sportId={currentView.slug}
                        competitionId={appSportsBookState.models.id.getCompetitionId(currentView.id)}
                        isCompetitionPage={true}
                    />
                </Wrapper>
            </LayoutSkeleton>
        </SectionWrapper>
    );
});
